import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Loader, MessageBlock } from '@getro/rombo';
import { useCompanyLayoutName } from 'hooks/useCompanyLayoutName';
import withMainLayout from '../../hocs/withMainLayout';
import { CompanyProfileLayout } from '../../components/layout/CompanyProfileLayout';
import { AdminPanel } from '../../components/organisms/adminPanel';
import useCompany from '../../hooks/useCompany';
import { activeNetworkSelector, userSelector } from '../../redux/selectors';
import usePermissions from '../../hooks/usePermissions';
import PageLayout from '../../components/layout/Page';
import strings from '../../strings';
import usePageHook from '../../hooks/usePageHook';

const CompanyAdmins = () => {
  const location = useLocation();
  const {
    company,
    error,
    initialized,
    companySlug,
    addAdmin,
    isAddingAdmin,
    removeAdmin,
    isRemovingAdmin,
    resendAdminInvite,
    isResendingAdminInvite,
  } = useCompany();
  const activeNetwork = useSelector(activeNetworkSelector);
  const currentUser = useSelector(userSelector);
  const permissions = usePermissions({ companySlug });
  usePageHook('Company admins');

  const { isInCompanyProfileOrNetwork } = useCompanyLayoutName(initialized);

  if (!initialized && !error) {
    return (
      <PageLayout>
        <PageLayout.Content>
          <Loader />
        </PageLayout.Content>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <PageLayout.Content>
          {error !== 404 && <MessageBlock type="error" content={strings.genericError} />}
          {error === 404 && (
            <MessageBlock
              type="warning"
              title="Company not found"
              content="It looks like the URL is not correct or the company doesn’t belong to the active network."
            />
          )}
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <CompanyProfileLayout
      isCompanyNetwork={activeNetwork.isCompany}
      description={strings.companyProfile.description}
      isInCompanyProfileOrNetwork={isInCompanyProfileOrNetwork}
    >
      <AdminPanel
        adminGroupName={company.name}
        isInvitingAdmin={isAddingAdmin}
        isDeletingAdmin={isRemovingAdmin}
        isResendingInvitation={isResendingAdminInvite}
        activeNetwork={activeNetwork}
        currentUser={currentUser}
        admins={company.managers}
        invitedUsers={company.invitedUsers}
        onRemoveAdmin={removeAdmin}
        onResendInvitation={resendAdminInvite}
        onAdminInvite={addAdmin}
        location={location}
        canAddCompanyAdmins={permissions.company.canAddCompanyAdmins}
        canRemoveCompanyAdmins={permissions.company.canRemoveCompanyAdmins}
        showEmailAddress
        company={company}
      />
    </CompanyProfileLayout>
  );
};

export default withMainLayout(CompanyAdmins);
